/**
 * The staff session, as React state.
 *
 * The lifecycle it implements:
 *
 *   cold load  → POST /admin/auth/refresh with the httpOnly cookie.
 *                200 → signed in, no login screen. 401 → show login.
 *                Unreachable API → an error screen with Retry, NOT a login
 *                screen: "we cannot tell" is not "you are signed out".
 *   signed in  → the access token is refreshed a minute before it expires, and
 *                again reactively if a request ever comes back 401.
 *   signed out → the API revokes the token family and clears the cookie.
 *
 * The access token itself never lands here; it stays inside the API client, in
 * memory. This provider holds only the identity needed to render the shell.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { AdminLoginRequest, AdminSessionUser } from '@wend/shared';

import { apiClient } from '../lib/api-client';
import { toApiError, type ApiError } from '../lib/api-error';

export type AuthStatus = 'checking' | 'ready' | 'error';

export interface AdminAuthValue {
  /** `checking` until the cold-load refresh has answered. */
  readonly status: AuthStatus;
  /** `null` when nobody is signed in. */
  readonly admin: AdminSessionUser | null;
  /** Only set when `status` is `error` — the API could not be reached. */
  readonly bootstrapError: ApiError | null;
  readonly retryBootstrap: () => void;
  /** Rejects with an `ApiError` the login form renders. */
  readonly signIn: (input: AdminLoginRequest) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const AuthContext = createContext<AdminAuthValue | null>(null);

export function useAdminAuth(): AdminAuthValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAdminAuth() must be used inside an <AuthProvider>.');
  }
  return value;
}

/** Refresh this long before the access token actually expires. */
const REFRESH_LEEWAY_SECONDS = 60;
/** Never schedule a refresh tighter than this, however short the token's life. */
const MIN_REFRESH_DELAY_SECONDS = 30;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [admin, setAdmin] = useState<AdminSessionUser | null>(null);
  const [bootstrapError, setBootstrapError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const timer = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /*
   * Registered before the bootstrap effect below, so the session that effect
   * establishes is already being observed when it arrives.
   */
  useEffect(() => {
    const unsubscribe = apiClient.subscribe((session) => {
      clearTimer();

      if (session === null) {
        setAdmin(null);
        return;
      }

      setAdmin(session.adminUser);

      const delay =
        Math.max(session.expiresIn - REFRESH_LEEWAY_SECONDS, MIN_REFRESH_DELAY_SECONDS) * 1000;

      timer.current = window.setTimeout(() => {
        // A silent refresh that fails is not fatal — the next request that gets
        // a 401 will try again, and that one has a person waiting on it.
        void apiClient.refreshSession().catch(() => undefined);
      }, delay);
    });

    return () => {
      unsubscribe();
      clearTimer();
    };
  }, [clearTimer]);

  useEffect(() => {
    let cancelled = false;

    setStatus('checking');
    setBootstrapError(null);

    apiClient.refreshSession().then(
      () => {
        if (!cancelled) setStatus('ready');
      },
      (error: unknown) => {
        if (cancelled) return;
        setBootstrapError(toApiError(error));
        setStatus('error');
      },
    );

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const value = useMemo<AdminAuthValue>(
    () => ({
      status,
      admin,
      bootstrapError,
      retryBootstrap: () => {
        setAttempt((current) => current + 1);
      },
      signIn: async (input: AdminLoginRequest) => {
        await apiClient.login(input);
      },
      signOut: async () => {
        await apiClient.logout();
      },
    }),
    [status, admin, bootstrapError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
